<template>
  <div>
    <div class="file-upload-wrapper">
      <div v-if="showNotification" class="download-notification">
        <span class="download-text">{{ notificationText }}</span>
        <button class="download-all-button" @click="downloadAll">
          <template v-if="isProcessing">
            <img src="/images/sf/converter/loader-white.svg" alt="" class="loading-icon button-loader" />
            <span>{{ downloadAllText }}</span>
          </template>
          <template v-else>
            <img
              :src="downloadAllClicked ? '/images/sf/converter/check.svg' : '/images/sf/converter/download.svg'"
              alt=""
              class="download-icon"
            />
            <span>{{ t('download-all') }}</span>
          </template>
        </button>
      </div>

      <form v-if="displayFiles.length === 0 && !showError" class="file-upload-form">
        <button class="file-upload-button" type="button" @click="triggerFileInput">
          {{ t('select-files') }}
        </button>
        <input
          class="file-upload-input"
          type="file"
          ref="fileInput"
          multiple
          @change="handleFileChange"
        />
        <label class="file-upload-label">
          <strong>{{ t('drag-and-drop-info') }}</strong>
          <br />
          <span class="file-upload-dragdrop">{{ t('auto-delete-note') }}</span>
        </label>
      </form>

      <div v-if="displayFiles.length > 0 && !showError" class="file-list" ref="fileList">
        <div
          v-for="(file, index) in displayFiles"
          :key="index"
          class="file-item"
          :class="{ 'file-item-loaded': file.isLoaded && isMobile }"
          :data-download-item="file.downloadUrl ? 'true' : undefined"
        >
          <div
            class="file-name-container"
            :class="{ 'file-name-error': file.error }"
          >
            <span class="file-name">{{ file.name }}</span>
            <span v-if="!file.error && !file.isConverting" class="file-size">
              {{ formatFileSize(file.size) }}
            </span>
            <span v-if="file.error" class="error-message">
              {{ file.errorMessage }}
            </span>
          </div>

          <span v-if="!file.isConverting && !file.downloadUrl && !file.error" class="file-to">
            {{ t('to') }}
          </span>

          <select
            v-if="!file.isConverting && !file.downloadUrl && !file.error"
            v-model="file.targetFormat"
            class="file-format"
          >
            <option v-for="format in file.availableFormats" :key="format" :value="format">
              {{ format.toUpperCase() }}
            </option>
          </select>

          <svg
            v-if="file.isConverting"
            class="progress-loader"
            width="36"
            height="36"
            viewBox="0 0 36 36"
          >
            <circle cx="18" cy="18" r="15.5" fill="none" stroke="#EEEEEE" stroke-width="3" />
            <circle
              cx="18" cy="18" r="15.5"
              fill="none"
              stroke="#7ED338"
              stroke-width="3"
              :stroke-dasharray="getProgressArc(fileProgress[file.name] || 0)"
              transform="rotate(-90 18 18)"
              style="transition: stroke-dasharray 0.5s ease"
            />
          </svg>

          <button
            v-if="file.downloadUrl"
            class="file-download"
            @click="downloadFile(file)"
          >
            <img
              :src="downloadClicked[file.name] ? '/images/sf/converter/check.svg' : '/images/sf/converter/download.svg'"
              alt="Download Icon"
              class="download-icon"
            />
            <span>{{ t('download') }}</span>
          </button>

          <button
            v-if="file.error && file.canRetry"
            class="retry-button"
            @click="retryConversion(file, index)"
          >
            <img src="/images/sf/converter/refresh.svg" alt="Retry" class="refresh-icon" />
            <span>{{ t('try-again') }}</span>
          </button>
        </div>
      </div>

      <button
        v-if="showConvertButton"
        class="convert-button"
        :disabled="isProcessing"
        :style="isProcessing ? { opacity: '0.72', pointerEvents: 'none' } : {}"
        @click="startConversion"
      >
        {{ isProcessing ? convertingText : t('convert') }}
      </button>

      <div v-if="showError" class="error-container">
        <img src="/images/sf/converter/alert.svg" alt="Error Icon" class="error-icon" />
        <h2 class="error-title">{{ errorTitle }}</h2>
        <p class="error-message-modal">{{ errorMessage }}</p>
        <button class="file-upload-button" @click="resetError">
          {{ t('select-files') }}
        </button>
      </div>
    </div>
    <div v-if="showConvertMore" class="button-wrapper">
      <button class="conv-more-btn" @click="convertMore">
        {{ t('convert-more') }}
      </button>
    </div>
  </div>
</template>

<script>
import translation from '../convert/i18.json';

export default {
  name: 'FileConverter',
  data() {
    return {
      files: [],
      displayFiles: [],
      originalFiles: new Map(),
      currentLang: 'id',
      langData: {},
      pageFormats: {
        '108': 'MP3',
        '109': 'MP4',
        '110': 'GIF',
        '111': 'MP4',
        '112': 'MP4'
      },
      validFormats: {
        MP4: ["mp3", "wav", "flac", "wma", "aiff", "opus", "avi", "mp2", "ogg", "ac3", "mpeg", "mov", "webm", "flv", "vob", "m4v", "gif", "m4a", "aac"],
        MP3: ["mp4", "wav"],
        GIF: ["mp4"],
        wav: ["mp4", "mp3"],
        flac: ["mp4"],
        wma: ["mp4"],
        aiff: ["mp4"],
        opus: ["mp4"],
        avi: ["mp4"],
        mkv: ["mp4"],
        mp2: ["mp4"],
        ogg: ["mp4"],
        ac3: ["mp4"],
        mpeg: ["mp4"],
        mov: ["mp4"],
        webm: ["mp4"],
        flv: ["mp4"],
        vob: ["mp4"],
        m4v: ["mp4"],
        m4a: ["mp4"],
        aac: ["mp4"]
      },
      showError: false,
      errorTitle: '',
      errorMessage: '',
      showNotification: false,
      notificationText: '',
      isProcessing: false,
      showConvertMore: false,
      isMobile: false,
      conversionStarted: false,
      fileProgress: {},
      convertingText: 'Converting.',
      dotsInterval: null,
      downloadAllText: 'Loading.',
      downloadAllDotsInterval: null,
      downloadAllClicked: false,
      downloadClicked: {},
    };
  },
  computed: {
    showConvertButton() {
      return this.files.length > 0 &&
             !this.showError &&
             (!this.conversionStarted || this.isProcessing);
    },
    downloadableFiles() {
      return this.displayFiles.filter(f => f.downloadUrl);
    },
  },
  mounted() {
    this.initLanguage();
    this.checkMobile();
    window.addEventListener('resize', this.checkMobile);
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.checkMobile);
    if (this.dotsInterval) clearInterval(this.dotsInterval);
    if (this.downloadAllDotsInterval) clearInterval(this.downloadAllDotsInterval);
  },
  methods: {
    initLanguage() {
      let detectedLang = 'id';
      if (window._sf && window._sf.lang && translation[window._sf.lang]) {
        detectedLang = window._sf.lang;
      } else if (translation['id']) {
        detectedLang = 'id';
      }
      this.currentLang = detectedLang;
      this.langData = translation[this.currentLang];
    },
    t(key) {
      return this.langData[key] || key;
    },
    checkMobile() {
      this.isMobile = window.innerWidth <= 480;
    },
    triggerFileInput() {
      this.$refs.fileInput.click();
    },
    handleFileChange(event) {
      const selectedFiles = Array.from(event.target.files);
      this.processFiles(selectedFiles);
    },
    processFiles(selectedFiles) {
      const maxSizeBytes = 1073741824;
      const validFiles = [];
      const invalidFiles = [];

      selectedFiles.forEach(file => {
        const fileExtension = file.name.split('.').pop().toLowerCase();
        const isSupported = (fileExtension in this.validFormats) ||
          Object.values(this.validFormats).some(formats => formats.includes(fileExtension));
        const isTooLarge = file.size > maxSizeBytes;

        if (isTooLarge) {
          if (selectedFiles.length === 1) {
            this.showErrorModal(this.t('too-large-title'), this.t('too-large-message'));
            return;
          }
          invalidFiles.push({
            name: file.name,
            error: true,
            errorMessage: this.t('reason-too-large'),
            canRetry: false
          });
          return;
        }

        if (!isSupported) {
          if (selectedFiles.length === 1) {
            this.showErrorModal(this.t('unsupported-title'), this.t('unsupported-message'));
            return;
          }
          invalidFiles.push({
            name: file.name,
            error: true,
            errorMessage: this.t('reason-unsupported'),
            canRetry: false
          });
          return;
        }

        const fileExtKey = Object.keys(this.validFormats).find(
          key => key.toLowerCase() === fileExtension
        );
        const availableFormats = fileExtKey
          ? this.validFormats[fileExtKey].map(f => f.toLowerCase())
          : [];

        validFiles.push({
          file: file,
          name: file.name,
          size: file.size,
          targetFormat: availableFormats[0] || 'mp4',
          availableFormats: availableFormats,
          isConverting: false,
          downloadUrl: null,
          error: false,
          isLoaded: false
        });
      });

      this.files = validFiles;
      this.displayFiles = [...validFiles, ...invalidFiles];

      validFiles.forEach(f => {
        this.originalFiles.set(f.name, f.file);
      });
    },
    async startConversion() {
      if (this.files.length === 0) return;

      this.$root.$emit('converter-start');

      this.conversionStarted = true;
      this.isProcessing = true;
      this.showNotification = this.files.length > 1;
      this.notificationText = this.t('processing');

      let cDots = 1;
      this.convertingText = 'Converting.';
      this.dotsInterval = setInterval(() => {
        cDots = cDots >= 3 ? 1 : cDots + 1;
        this.convertingText = 'Converting' + '.'.repeat(cDots);
      }, 500);

      let dlDots = 1;
      this.downloadAllText = 'Loading.';
      this.downloadAllDotsInterval = setInterval(() => {
        dlDots = dlDots >= 3 ? 1 : dlDots + 1;
        this.downloadAllText = 'Loading' + '.'.repeat(dlDots);
      }, 500);

      this.displayFiles = this.displayFiles.map(f =>
        f.file ? { ...f, isConverting: true } : f
      );

      this.files.forEach(fileData => {
        this.$set(this.fileProgress, fileData.name, 0);
        this.startProgressSimulation(fileData.name);
      });

      const promises = this.files.map(fileData => this.convertFile(fileData));
      const results = await Promise.allSettled(promises);

      if (this.dotsInterval) { clearInterval(this.dotsInterval); this.dotsInterval = null; }
      if (this.downloadAllDotsInterval) { clearInterval(this.downloadAllDotsInterval); this.downloadAllDotsInterval = null; }

      this.displayFiles = this.displayFiles.map((f, index) => {
        if (!f.file) return f;
        const result = results[index];
        if (result.status === 'fulfilled') {
          return {
            ...f,
            isConverting: false,
            downloadUrl: result.value.downloadUrl,
            name: result.value.name,
            isLoaded: true
          };
        } else {
          return {
            ...f,
            isConverting: false,
            error: true,
            errorMessage: this.t('not-converted'),
            canRetry: true,
            isLoaded: true
          };
        }
      });

      const downloadCount = this.displayFiles.filter(f => f.downloadUrl).length;
      if (this.showNotification) {
        this.notificationText = `${downloadCount} file(s) ready for download!`;
      }

      this.isProcessing = false;
      this.showConvertMore = true;
      this.$emit('conversion-complete', { downloadCount });
    },
    async convertFile(fileData) {
      const typeTo = fileData.targetFormat;
      const baseUrl = WEBPACK_CONVERTER_URL;

      this.$root.$emit('converter-request');

      try {
        const initRes = await fetch(`${baseUrl}/convert/upload`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            filename: fileData.file.name,
            targetExt: typeTo,
            filesize: fileData.file.size,
          }),
        });

        if (!initRes.ok) {
          const err = await initRes.json().catch(() => ({}));
          throw new Error(err.message || `Upload init failed: ${initRes.status}`);
        }

        const { jobId, parts, partSize } = await initRes.json();

        const uploadedParts = await this.uploadPartsToS3(fileData.file, parts, partSize, (ratio) => {
          const current = this.fileProgress[fileData.name] || 0;
          const target = Math.round(ratio * 50);
          if (target > current) this.$set(this.fileProgress, fileData.name, target);
        });

        const completeRes = await fetch(`${baseUrl}/convert/${jobId}/complete`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ parts: uploadedParts }),
        });

        if (!completeRes.ok) {
          const err = await completeRes.json().catch(() => ({}));
          throw new Error(err.message || `Complete failed: ${completeRes.status}`);
        }

        await this.waitForConversion(jobId, (progress) => {
          const current = this.fileProgress[fileData.name] || 0;
          const target = 50 + Math.round(progress * 0.5);
          if (target > current) this.$set(this.fileProgress, fileData.name, target);
        });

        const newName = fileData.name.replace(/\.[^.]+$/, `.${typeTo}`);
        this.$root.$emit('converter-request-success');

        return {
          name: newName,
          downloadUrl: `${baseUrl}/convert/${jobId}/download`,
        };
      } catch (error) {
        this.$root.$emit('converter-request-failed');
        throw { name: fileData.name, error: true };
      }
    },
    async uploadPartsToS3(file, parts, partSize, onProgress) {
      const results = new Array(parts.length);
      let uploadedBytes = 0;
      const queue = [...parts];

      await Promise.all(
        Array.from({ length: Math.min(8, parts.length) }, async () => {
          while (queue.length > 0) {
            const part = queue.shift();
            const start = (part.partNumber - 1) * partSize;
            const chunk = file.slice(start, Math.min(start + partSize, file.size));

            const res = await fetch(part.url, { method: 'PUT', body: chunk });
            if (!res.ok) throw new Error(`Part ${part.partNumber} upload failed: ${res.status}`);

            const etag = res.headers.get('ETag') || res.headers.get('etag') || '';
            results[part.partNumber - 1] = { partNumber: part.partNumber, etag };

            uploadedBytes += chunk.size;
            onProgress(uploadedBytes / file.size);
          }
        })
      );

      return results;
    },
    waitForConversion(jobId, onProgress) {
      const baseUrl = WEBPACK_CONVERTER_URL;
      return new Promise((resolve, reject) => {
        const es = new EventSource(`${baseUrl}/convert/${jobId}/events`);

        es.onmessage = (event) => {
          try {
            const data = JSON.parse(event.data);
            if (data.progress) onProgress(data.progress);

            if (data.state === 'completed') {
              es.close();
              resolve();
            } else if (data.state === 'failed' || data.state === 'missing') {
              es.close();
              reject(new Error('Conversion failed'));
            }
          } catch (e) {
            es.close();
            reject(e);
          }
        };

        es.onerror = () => {
          es.close();
          reject(new Error('SSE connection error'));
        };
      });
    },
    startProgressSimulation(fileName) {
      const steps = [
        { target: 25, delay: 200 },
        { target: 50, delay: 1200 },
        { target: 70, delay: 3500 },
        { target: 82, delay: 7000 },
      ];
      steps.forEach(({ target, delay }) => {
        setTimeout(() => {
          const current = this.fileProgress[fileName] || 0;
          if (current < target) this.$set(this.fileProgress, fileName, target);
        }, delay);
      });
    },
    getProgressArc(percent) {
      const r = 15.5;
      const circumference = 2 * Math.PI * r;
      const filled = (percent / 100) * circumference;
      return `${filled} ${circumference}`;
    },
    downloadFile(file) {
      const ext = file.name.split('.').pop().toLowerCase();
      this.$root.$emit('converter-click', ext);

      if (!this.downloadClicked[file.name]) {
        this.$set(this.downloadClicked, file.name, true);
        setTimeout(() => this.$set(this.downloadClicked, file.name, false), 3000);
      }

      const win = window.open(file.downloadUrl, '_blank');
      if (!win || win.closed || typeof win.closed === 'undefined') {
        const link = document.createElement('a');
        link.href = file.downloadUrl;
        link.target = '_blank';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    },
    downloadAll() {
      this.$root.$emit('converter-click-all');

      if (!this.downloadAllClicked) {
        this.downloadAllClicked = true;
        setTimeout(() => { this.downloadAllClicked = false; }, 3000);
      }

      let delay = 0;
      this.downloadableFiles.forEach(file => {
        if (file.downloadUrl) {
          setTimeout(() => {
            const link = document.createElement('a');
            link.href = file.downloadUrl;
            link.download = file.name || 'downloaded-file';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
          }, delay);
          delay += 300;
        }
      });
    },
    async retryConversion(file, index) {
      const originalFile = this.originalFiles.get(file.name);
      if (!originalFile) return;

      this.$set(this.fileProgress, file.name, 0);
      this.startProgressSimulation(file.name);

      this.displayFiles[index] = {
        ...this.displayFiles[index],
        isConverting: true,
        error: false
      };

      const fileData = {
        file: originalFile,
        name: file.name,
        size: originalFile.size,
        targetFormat: file.targetFormat
      };

      try {
        const result = await this.convertFile(fileData);
        this.displayFiles[index] = {
          ...this.displayFiles[index],
          isConverting: false,
          downloadUrl: result.downloadUrl,
          name: result.name,
          isLoaded: true
        };

        if (this.showNotification) {
          const downloadCount = this.displayFiles.filter(f => f.downloadUrl).length;
          this.notificationText = `${downloadCount} file(s) ready for download!`;
        }
      } catch (error) {
        this.displayFiles[index] = {
          ...this.displayFiles[index],
          isConverting: false,
          error: true,
          errorMessage: this.t('not-converted')
        };
      }
    },
    showErrorModal(title, message) {
      this.errorTitle = title;
      this.errorMessage = message;
      this.showError = true;
    },
    resetError() {
      this.showError = false;
      this.files = [];
      this.displayFiles = [];
      this.originalFiles.clear();
      this.conversionStarted = false;
      if (this.$refs.fileInput) this.$refs.fileInput.value = '';
    },
    convertMore() {
      location.reload();
    },
    formatFileSize(size) {
      if (size < 1024) {
        return size + ' B';
      } else if (size < 1024 * 1024) {
        return (size / 1024).toFixed(2) + ' KB';
      } else {
        return (size / (1024 * 1024)).toFixed(2) + ' MB';
      }
    }
  }
};
</script>

<style scoped>
.file-upload-wrapper {
  width: 100%;
  max-width: 824px;
  min-height: 200px;
  border: 2px solid #ccc;
  border-radius: 10px;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  justify-content: center;
  position: relative;
  background-color: #007BFF08;
  margin: auto;
  box-sizing: border-box;
  margin-bottom: 50px;
  padding: 20px;
}

.file-upload-form {
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
}

.file-upload-input {
  display: none;
}

.file-upload-label {
  cursor: pointer;
  font-size: 16px;
  margin-top: 20px;
  color: #333;
}

.file-upload-dragdrop {
  color: #888;
}

.file-upload-button {
  margin-top: 10px;
  padding: 10px 20px;
  font-size: 16px;
  background-color: #7ED338;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
}

.file-upload-button:hover {
  background-color: #6CC12E;
}

.file-list {
  width: 100%;
}

.file-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  max-width: 100%;
  padding: 10px 15px;
  background: white;
  border-radius: 5px;
  margin-top: 5px;
  background: transparent;
  box-sizing: border-box;
}

.file-name-container {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  height: 48px;
  padding: 10px;
  border: 1px solid #ccc;
  border-radius: 4px;
  background: white;
  box-sizing: border-box;
  overflow: hidden;
}

.file-name {
  flex-grow: 1;
  flex-shrink: 1;
  border: none;
  outline: none;
  font-size: 16px;
  background: transparent;
  height: 100%;
  display: flex;
  align-items: center;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  max-width: calc(100% - 180px);
}

.file-size {
  margin-left: 10px;
  font-size: 14px;
  color: #666;
  white-space: nowrap;
  flex-shrink: 0;
}

.file-to {
  margin: 0 10px;
  font-size: 16px;
  line-height: 48px;
  flex-shrink: 0;
}

.file-format {
  width: 120px;
  height: 48px;
  padding: 10px;
  border-radius: 4px;
  border: 1px solid #ccc;
  font-size: 16px;
  box-sizing: border-box;
  flex-shrink: 0;
}

.convert-button {
  margin-top: 20px;
  padding: 12px 24px;
  font-size: 18px;
  background-color: #7ED338;
  color: white;
  border: none;
  border-radius: 5px;
  cursor: pointer;
  width: 200px;
}

.convert-button:hover {
  background-color: #6CC12E;
}

.progress-loader {
  display: inline-block;
  vertical-align: middle;
  margin-left: 10px;
  flex-shrink: 0;
}

.loading-icon {
  width: 50px;
  height: 50px;
  margin-left: 20px;
  flex-shrink: 0;
}

@media (max-width: 480px) {
  .loading-icon {
    margin-left: 15px;
    width: 40px;
    height: 40px;
  }
}

.file-download {
  height: 48px;
  max-width: 180px;
  padding: 12px 24px;
  font-size: 16px;
  background-color: #7ED338;
  color: white;
  border: none;
  border-radius: 5px;
  cursor: pointer;
  flex-grow: 1;
  text-align: center;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-left: 10px;
  gap: 8px;
  flex-shrink: 0;
}

.file-download:hover {
  background-color: #6CC12E;
}

.download-notification {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  padding: 10px 15px;
  width: 100%;
  max-width: 752px;
  background-color: #226CAA;
  color: white;
  border-radius: 5px;
  margin-top: 15px;
  box-sizing: border-box;
}

.file-name-error {
  border: 2px solid #d9534f !important;
}

.error-message {
  font-size: 14px;
  color: #d9534f;
  margin-left: 10px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  flex-shrink: 0;
}

.retry-button {
  height: 48px;
  max-width: 145px;
  min-width: 145px;
  padding: 12px 24px;
  font-size: 15px;
  background-color: #d9534f;
  color: white;
  border: none;
  border-radius: 5px;
  cursor: pointer;
  text-align: center;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-left: 10px;
  gap: 8px;
  flex-shrink: 0;
}

.retry-button:hover {
  background-color: #c9302c;
}

.download-text {
  font-size: 16px;
  font-weight: bold;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.download-all-button {
  padding: 10px 15px;
  font-size: 14px;
  background-color: #7ED338;
  color: white;
  border: none;
  border-radius: 5px;
  font-weight: bold;
  cursor: pointer;
  flex-shrink: 0;
  margin-left: auto;
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

.download-all-button:hover {
  background-color: #6CC12E;
}

.error-container {
  width: 100%;
  min-height: 200px;
  border-radius: 10px;
  padding: 40px 20px;
  background: white;
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.1);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  text-align: center;
  overflow-wrap: break-word;
  margin: -20px;
  width: calc(100% + 40px);
  max-width: calc(100% + 40px);
}

.error-icon {
  width: 50px;
  height: 50px;
  margin-bottom: 15px;
}

.error-title {
  font-size: 20px;
  font-weight: bold;
  margin: 0 0 10px 0;
  color: #333;
}

.error-message-modal {
  font-size: 16px;
  color: #666;
  margin: 0 0 25px 0;
  line-height: 1.5;
  max-width: 90%;
}

.download-icon {
  width: 18px;
  height: 18px;
}

.refresh-icon {
  width: 24px;
  height: 24px;
}

.spin-animation {
  animation: spin 1s linear infinite;
}

.button-loader {
  width: 24px;
  height: 24px;
}

.button-wrapper {
  display: flex;
  text-align: center;
  justify-content: center;
  margin-bottom: 50px;
}

.conv-more-btn {
  height: 48px;
  width: 148px;
  border: 2px solid #7ED338;
  border-radius: 4px;
  background: transparent;
  color: #7ED338;
  cursor: pointer;
  font-size: 16px;
}

.conv-more-btn:hover {
  background-color: #7ED33810;
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

@media (max-width: 480px) {
  .download-notification {
    flex-direction: column;
    align-items: flex-start;
    gap: 10px;
  }

  .download-text {
    text-align: left;
    width: 100%;
  }

  .download-all-button {
    width: 100%;
    text-align: center;
  }

  .file-item-loaded {
    flex-direction: column;
    align-items: flex-start;
  }

  .file-item-loaded .file-name-container {
    width: 100%;
  }

  .file-item-loaded .file-download {
    width: 100%;
    max-width: 390px;
    margin-top: 10px;
    margin-left: 1px;
  }

  .file-item-loaded .retry-button {
    width: 100%;
    max-width: 390px;
    margin-top: 10px;
    margin-left: 1px;
  }

  .error-container {
    padding: 30px 15px;
  }

  .error-message-modal {
    font-size: 14px;
  }
}
</style>
